import $ from 'jquery';

import Localize from 'app/src/localize/Localize';
import Terminal, { TerminalTransactionResponse } from 'app/src/payments/terminals/Terminal';

const _Status = {
  APPROVED: 'APPROVED',
  DECLINED: 'DECLINED',
  DECLINED_DUPLICATE: 'DECLINED,DUPLICATE',
  POS_CANCELLED: 'POSCANCELLED',
  USER_CANCELLED: 'USERCANCELLED',
  CANCELLED: 'CANCELLED',
  FAILED: 'FAILED',
  ERROR: 'ERROR',
  ONLINE: 'ONLINE',
};

const _Errors = {
  INVALID_TERMINAL_ERROR: 'Invalid Terminal Serial Number',
  TERMINAL_BUSY: 'Invalid Request:Terminal Busy',
};

const _REQUEST_TIMEOUT = 5000; // ms
const _DESCRIPTION = 'Cayan Genius Payment Terminal';
const _UNAVAILABLE = 'Unavailable';

export default class CayanGeniusTerminal extends Terminal {
  static get Errors() {
    return _Errors;
  }

  /**
   * Constructor for a CayanGeniusTerminal object.
   *
   * @param terminalModel {CCTerminal}    The API model representation of the terminal
   */
  constructor(terminalModel) {
    super(terminalModel);
  }

  /**
   * Communicates with the terminal to start a transaction.  A Cayan Genius transaction is initiated
   * by first requesting a transport key from the API (which obtains one from the Cayan service).  This
   * also creates a credit charge record which we use to later construct a payment.  The transport key
   * is passed to the terminal to initiate the transaction.  Once the transaction has been completed,
   * a terminal payment is constructed with the credit charge ID and the raw response from the
   * server.
   *
   * @returns {jQuery.Promise}    Resolves if the transaction was successfully process otherwise
   *                              the promise is rejected.  In either case, a TerminalTransactionResponse
   *                              is resolved or rejected with the promise.
   */
  processPayment({ amount, saleID, gatewayConfig, tipAmount = null }) {
    let deferred = $.Deferred();

    if (saleID && gatewayConfig && gatewayConfig.id) {
      // make sure we can talk to the terminal first
      this.checkStatus().always((status) => {
        if (status === Terminal.Status.ONLINE) {
          // TODO: Update regex when Cayan fixes their bug: /[^a-zA-Z0-9]/g
          let terminalKey = this.model.get('name').replace(/\D/g, '').slice(0, 15);

          // Create a transaction using the API.  This will return the transport key and the
          // charge ID that was created.  We use the transport key to initiate and validate
          // a transaction with the terminal.
          gatewayConfig
            .createGeniusTransaction(amount, saleID, terminalKey)
            .done((keys) => {
              let processTransactionUrl = this._getTerminalRequestUrl(
                `v2/pos?TransportKey=${keys.transportKey}&Format=JSON`
              );

              this._initiateTerminalRequest(processTransactionUrl).always((rawResponse) => {
                let payment = this._createPayment(
                  amount,
                  keys.ccChargeID,
                  rawResponse && rawResponse.Status ? rawResponse : JSON.stringify({}),
                  saleID,
                  tipAmount
                );
                payment.model.set('saleID', saleID);

                if (rawResponse.Status) {
                  switch (rawResponse.Status.toUpperCase()) {
                    case _Status.POS_CANCELLED:
                      deferred.reject(
                        new TerminalTransactionResponse(
                          Terminal.Response.CANCELLED,
                          rawResponse.Status,
                          payment
                        )
                      );
                      break;

                    case _Status.USER_CANCELLED:
                      deferred.reject(
                        new TerminalTransactionResponse(
                          Terminal.Response.CANCELLED_FROM_TERMINAL,
                          rawResponse.Status,
                          payment
                        )
                      );
                      break;

                    case _Status.FAILED:
                      let response = Terminal.Response.TERMINAL_ERROR;
                      let errorMessage = rawResponse.ErrorMessage;
                      if (
                        rawResponse.ErrorMessage === _Errors.INVALID_TERMINAL_ERROR ||
                        rawResponse.ErrorMessage === _Errors.TERMINAL_BUSY
                      ) {
                        response = Terminal.Response.COULD_NOT_START_TERMINAL;
                      }

                      if (rawResponse.ErrorMessage === _Errors.TERMINAL_BUSY) {
                        errorMessage = Localize.translate('Terminal is busy, please retry later.');
                      }

                      deferred.reject(
                        new TerminalTransactionResponse(
                          response,
                          rawResponse.Status,
                          payment,
                          errorMessage
                        )
                      );
                      break;

                    case _Status.APPROVED:
                      // Cayan can have a status of approved but with an error message;
                      // go figure
                      if (rawResponse.ErrorMessage) {
                        deferred.resolve(
                          new TerminalTransactionResponse(
                            Terminal.Response.APPROVED_WITH_ERROR,
                            rawResponse.Status,
                            payment,
                            rawResponse.ErrorMessage
                          )
                        );
                      } else {
                        deferred.resolve(
                          new TerminalTransactionResponse(
                            Terminal.Response.APPROVED,
                            rawResponse.Status,
                            payment
                          )
                        );
                      }
                      break;

                    default:
                      deferred.reject(
                        new TerminalTransactionResponse(
                          Terminal.Response.DECLINED,
                          rawResponse.Status,
                          payment,
                          rawResponse.ErrorMessage
                        )
                      );
                      break;
                  }
                } else {
                  // If we don't have a Status assume that we've had a timeout
                  deferred.reject(
                    new TerminalTransactionResponse(Terminal.Response.TIMEOUT, '', payment, '')
                  );
                }
              });
            })
            .fail((response) => {
              // pass along the API-reported error without the file and line number
              let error =
                response && response.responseJSON && response.responseJSON.message
                  ? response.responseJSON.message.replace(/ *\([^)]*\) */g, '')
                  : '';

              deferred.reject(
                new TerminalTransactionResponse(Terminal.Response.ERROR, _Status.ERROR, null, error)
              );
            });
        } else {
          let errorMessage =
            status === Terminal.Status.DISCONNECTED
              ? Localize.translate('Terminal is offline, the transaction could not be started.')
              : Localize.translate('The transaction could not be started.');

          deferred.reject(
            new TerminalTransactionResponse(
              Terminal.Response.ERROR,
              _Status.ERROR,
              null,
              errorMessage
            )
          );
        }
      });
    } else {
      throw 'A sale ID and gateway ID is required to process a payment for a Cayan Genius terminal';
    }

    return deferred.promise();
  }

  /**
   * Communicates with the terminal to cancel the request.  Resolves the returned promise if the
   * transaction was successfully cancelled.
   *
   * @returns {jQuery.Promise}    Resolves if the transaction was successfully cancelled otherwise
   *                              the promise is rejected.
   */
  cancelTransaction() {
    let deferred = $.Deferred();
    let cancelRequestUrl = this._getTerminalRequestUrl('v1/pos?Action=Cancel&Format=JSON');

    this._initiateTerminalRequest(cancelRequestUrl, _REQUEST_TIMEOUT)
      .done((response) => {
        if (response && response.Status && response.Status.toUpperCase() === _Status.CANCELLED) {
          deferred.resolve();
        } else {
          deferred.reject();
        }
      })
      .fail(() => {
        deferred.reject();
      });

    return deferred.promise();
  }

  /**
   * Communicates with the terminal to check the status of the terminal.  Resolves with the appropriate
   * status.
   *
   * @returns {jQuery.Promise}    Resolves with the appropriate Terminal.Status.  The promise is never
   *                              rejected.
   */
  checkStatus() {
    let deferred = $.Deferred();
    let statusRequestUrl = this._getTerminalRequestUrl('v2/pos?Action=Status&Format=JSON');

    this._initiateTerminalRequest(statusRequestUrl, _REQUEST_TIMEOUT)
      .done((response) => {
        if (response && response.Status && response.Status.toUpperCase() !== _Status.ERROR) {
          // the response contains details about the terminal; we'll use that to store
          // information that we expose via the info method
          this._hardwareDetails = response;

          if (response.Status.toUpperCase() === _Status.ONLINE) {
            deferred.resolve(Terminal.Status.ONLINE);
          } else {
            deferred.resolve(Terminal.Status.BUSY);
          }
        } else {
          deferred.resolve(Terminal.Status.ERROR);
        }
      })
      .fail(() => {
        deferred.resolve(Terminal.Status.DISCONNECTED);
      });

    return deferred.promise();
  }

  /**
   * Retrieves a descriptive string about the terminal type.
   *
   * @returns {string}    The string identifying the terminal type
   */
  description() {
    return _DESCRIPTION;
  }

  /**
   * Retrieves data about the terminal  Typically used for analytics purposes.  The data necessary is
   * populated when a checkStatus() call is made.
   *
   * @returns {Object}    An object containing details about the terminal.
   *          {Object.appVersion} The application version as reported from the terminal
   *          {Object.OSVersion}  The OS version as reported from the terminal
   *          {Object.serialNumber}   The serial number of the terminal as reported from the terminal
   */
  info() {
    let info = super.info();

    info.appVersion = this._hardwareDetails
      ? this._hardwareDetails.ApplicationVersion
      : _UNAVAILABLE;
    info.OSVersion = this._hardwareDetails ? this._hardwareDetails.OSVersion : _UNAVAILABLE;
    info.serialNumber = this._hardwareDetails ? this._hardwareDetails.SerialNumber : _UNAVAILABLE;

    return info;
  }

  /**
   * Formulates a request URL to communicate with a terminal.
   *
   * @param action {String}   The particular action to be performed.  This is a fragment to be
   *                          attached to the terminal URL.
   * @returns {String}        The terminal request URL
   * @private
   */
  _getTerminalRequestUrl(action) {
    return `https://${this.model.configuration.address}:8443/${action}`;
  }

  /**
   * Contacts the physical terminal to initialize payments, cancel payments,
   * check status, etc.
   *
   * @param url {String} The URL to contact
   * @param timeout {Number} The value in milliseconds before the request
   * @private
   */
  _initiateTerminalRequest(url, timeout = null) {
    var ajaxOptions = {
      type: 'GET',
      url,
    };

    if (timeout) {
      ajaxOptions.timeout = timeout;
    }

    return $.ajax(ajaxOptions);
  }
}
